/**
 * Tool Search Configuration
 *
 * Defines which tools are loaded immediately (non-deferred) vs on-demand (deferred).
 * This configuration enables Anthropic's tool search feature to reduce context bloat
 * by 85%+ while maintaining high tool selection accuracy.
 *
 * Non-deferred tools (~500 tokens):
 * - Loaded immediately in every request
 * - Should be the 3-5 most frequently used tools
 * - Typically query/read operations
 *
 * Deferred tools (~0 tokens until discovered):
 * - Loaded on-demand when Claude searches for them
 * - Can be edit/write operations or specialized queries
 * - Discovered via tool search when needed
 */

export type ToolSearchVariant = 'regex' | 'bm25';

export interface ToolSearchConfig {
  /** Which tool search variant to use */
  variant: ToolSearchVariant;

  /** Tools loaded immediately (non-deferred) */
  nonDeferredTools: string[];

  /** Tools loaded on-demand (deferred) */
  deferredTools: string[];
}

/**
 * Default tool search configuration
 *
 * Non-deferred tools are the most frequently used query operations.
 * Deferred tools include edit operations (which require confirmation anyway)
 * and specialized queries that are used less frequently.
 */
export const DEFAULT_TOOL_SEARCH_CONFIG: ToolSearchConfig = {
  variant: 'bm25', // Use BM25 for natural language queries (more flexible than regex)

  // Non-deferred (HOT): the minimal set that must be eager. Anthropic's guidance
  // is "3–5 hot tools"; we keep 7, chosen by a strict rule, not by "sales-ish".
  //
  // A tool earns a hot slot ONLY if deferring it would add a `tool_search`
  // round-trip (an extra model call under our tight ITPM limit) to a FREQUENT
  // flow. Two cases qualify:
  //   1. First-hop lookups that START almost every chain and can't be
  //      discovered mid-chain — search_crm (catch-all), find_contact,
  //      find_company, list_deals.
  //   2. The two high-frequency find→create ACTIONS whose deferral would break
  //      the exact reported failure ("log a deal for X" = find→create) —
  //      create_deal, create_contact — plus log_communication (the end of the
  //      most common "log a call/meeting" chain).
  //
  // Everything else DEFERS and is discovered on demand: the cost is one
  // tool_search hop the first time it's needed in a turn, which is acceptable
  // for lower-frequency / analytics / specialized tools. Previously-hot tools
  // moved to deferred (2026-07): find_deal (search_crm/list_deals cover it),
  // list_contacts (search_crm covers it), get_deals_by_stage + list_followup_leads
  // (analytics/specialized), update_deal (less frequent than create), create_ticket
  // + create_time_entry (engineer flow, not the sales daily loop).
  //
  // Trimming 14→7 removes ~6k tokens from the always-on cacheable prefix (paid on
  // every cache-write and read). Cache reads are ITPM-exempt, so the hot set's
  // real cost is the one-time write per 5-min window; keeping it small is a net
  // win UNLESS hop frequency spikes. RE-ADD a tool here if the meridian_tool_io /
  // onStepFinish logs show tool_search hopping to it often on common flows.
  // Keep this list curated and stable (reordering/adding invalidates the cache prefix).
  nonDeferredTools: [
    // First-hop lookups (start almost every chain)
    'search_crm', // universal full-text search — the catch-all first hop
    'find_contact',
    'find_company',
    'list_deals',
    // High-frequency actions (deferral would add a hop to common flows)
    'create_deal', // "log a deal for X" = find_company → create_deal, kept hop-free
    'log_communication', // "log a call/meeting with X" = find_contact → log
    // UI-signal tool: must be callable BEFORE any other tool on multi-step
    // work (it announces the plan), so it can never sit behind a tool_search
    // hop. Near-zero schema cost (~120 tokens).
    'update_plan',
    // NOTE: create_contact was demoted to deferred (2026-07) — less frequent than
    // create_deal, and its find→create flow tolerates one tool_search hop. Re-add
    // if usage_events shows it hopping often.
  ],

  // Deferred: representative on-demand tools (~0 tokens until discovered).
  // NOTE: `deferredTools` is INFORMATIONAL ONLY — partitionToolIds/isHotTool
  // derive "deferred" as "NOT in nonDeferredTools", so this list gates nothing.
  // It must NOT repeat a tool that is also hot (reads as a contradiction).
  // create_deal/update_deal/find_deal/get_deals_by_stage were removed here
  // because they are in the hot set above.
  deferredTools: [
    // Edit tools — require confirmation anyway
    'edit_contact',
    'edit_company',
    'create_contact_activity',
    'mark_deal_won',
    'mark_deal_lost',
    'update_deal',

    // Lower-frequency reads/actions demoted from hot (2026-07)
    'find_deal',
    'list_contacts',
    'get_deals_by_stage',
    'list_followup_leads',
    'create_ticket',
    'create_time_entry',
    'create_contact',

    // Search/Analytics — specialized queries
    'search_deals',
    'get_deal_analytics',
    'audit_contact_timeline',
  ],
};

/**
 * Alternative configuration for regex-based tool search
 * Use this if you prefer Claude to construct regex patterns
 */
export const REGEX_TOOL_SEARCH_CONFIG: ToolSearchConfig = {
  variant: 'regex',
  nonDeferredTools: DEFAULT_TOOL_SEARCH_CONFIG.nonDeferredTools,
  deferredTools: DEFAULT_TOOL_SEARCH_CONFIG.deferredTools,
};

/**
 * Configuration for traditional tool loading (all tools non-deferred)
 * Use this as a fallback or for testing
 */
export const TRADITIONAL_TOOL_CONFIG: ToolSearchConfig = {
  variant: 'bm25',
  nonDeferredTools: [
    'find_contact',
    'find_company',
    'search_crm',
    'list_contacts',
    'list_deals',
    'edit_contact',
    'edit_company',
    'create_contact_activity',
    'create_deal',
    'update_deal',
    'mark_deal_won',
    'mark_deal_lost',
    'search_deals',
    'get_deal_analytics',
    'get_deals_by_stage',
    'audit_contact_timeline',
    'find_deal',
  ],
  deferredTools: [],
};

/**
 * Get tool search configuration based on environment
 */
export function getToolSearchConfig(
  useToolSearch: boolean = true,
  variant: ToolSearchVariant = 'bm25'
): ToolSearchConfig {
  if (!useToolSearch) {
    return TRADITIONAL_TOOL_CONFIG;
  }

  return variant === 'regex' ? REGEX_TOOL_SEARCH_CONFIG : DEFAULT_TOOL_SEARCH_CONFIG;
}

/**
 * Validate tool search configuration
 */
export function validateToolSearchConfig(config: ToolSearchConfig): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check that we have at least one non-deferred tool
  if (config.nonDeferredTools.length === 0) {
    errors.push('At least one tool must be non-deferred');
  }

  // Check for duplicate tools
  const allTools = [...config.nonDeferredTools, ...config.deferredTools];
  const duplicates = allTools.filter((tool, index) => allTools.indexOf(tool) !== index);
  if (duplicates.length > 0) {
    errors.push(`Duplicate tools found: ${duplicates.join(', ')}`);
  }

  // Check for valid variant
  if (config.variant !== 'regex' && config.variant !== 'bm25') {
    errors.push(`Invalid variant: ${config.variant}. Must be 'regex' or 'bm25'`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * A tool is HOT (loaded eagerly into the prompt) iff it appears in `nonDeferredTools`.
 * Every other tool defers — its schema is withheld until Claude surfaces it through the
 * tool-search server tool. The hot list is the single source of truth, so a newly added
 * tool defers automatically without being enumerated anywhere.
 */
export function isHotTool(toolId: string, config: ToolSearchConfig): boolean {
  return config.nonDeferredTools.includes(toolId);
}

/**
 * Partition all available tool ids into eager (`hot`) vs `deferred` per the config's hot list.
 * `deferred` is every id NOT in `nonDeferredTools`; the config's own `deferredTools` field is
 * informational only and does not constrain this.
 */
export function partitionToolIds(
  allToolIds: readonly string[],
  config: ToolSearchConfig
): { hot: string[]; deferred: string[] } {
  const hot: string[] = [];
  const deferred: string[] = [];
  for (const id of allToolIds) {
    if (isHotTool(id, config)) hot.push(id);
    else deferred.push(id);
  }
  return { hot, deferred };
}

/**
 * Anthropic's tool search tool (`defer_loading`) requires Opus 4.5+ / Sonnet 4.5+ /
 * Haiku 4.5 / Fable 5 / Mythos 5. On Opus 4.1 and earlier, `deferLoading` is NOT
 * supported — the deferred tool schemas silently stay in context, so we pay full
 * token cost and get NONE of the savings tool search exists for. See
 * https://platform.claude.com/docs/en/agents-and-tools/tool-use/tool-search-tool
 * (Model compatibility) and the MERIDIAN_TOOL_SEARCH rule.
 *
 * Matching is on the model-id string and is format-agnostic (accepts bare
 * first-party ids like `claude-opus-4-5`, alias `claude-opus-4-5`, and Bedrock
 * inference-profile ids like `us.anthropic.claude-opus-4-5-20251101-v1:0`).
 * Returns false for anything we can't positively confirm is capable, so a
 * regression to an old model id is flagged loudly rather than passing silently.
 */
export const TOOL_SEARCH_CAPABLE_MODEL_TOKENS: readonly string[] = [
  'opus-4-5',
  'opus-4-6',
  'opus-4-7',
  'opus-4-8',
  'sonnet-4-5',
  'sonnet-4-6',
  'haiku-4-5',
  'fable-5',
  'mythos-5',
];

export function isToolSearchCapableModel(modelId: string | undefined | null): boolean {
  if (!modelId) return false;
  const id = modelId.toLowerCase();
  return TOOL_SEARCH_CAPABLE_MODEL_TOKENS.some((token) => id.includes(token));
}
